// src/context/AccountWatermarksContext.tsx
/**
 * Ambient freshness probe for the whole tab.
 *
 * A write that happens somewhere other than this browser tab — an assistant
 * over MCP, a colleague in another tab, a background job — is invisible here,
 * because every page fetches its lists once on mount and then never asks
 * again. `GET /api/accounts/watermarks` is the cheap question "did anything I
 * am showing move?"; this provider asks it on a timer and tells subscribers
 * which lists to refetch.
 *
 * Design notes, each one load-bearing:
 *
 * - **Inequality, never "newer".** `max_updated_at` is written by the
 *   application, not the database, and the backend runs on several instances
 *   whose clocks are only approximately equal — the value can move backwards.
 *   "Different from what I had" is the only comparison that survives that.
 * - **The first response is the baseline and announces nothing.** Otherwise
 *   every page reload would report the whole morning's history as news.
 * - **The fast tier is an expiring window, not a sticky flag.**
 *   `useAutoRefresh` re-arms its fast budget on a `fast` false→true *edge*, so
 *   a watermark that keeps moving must be able to produce that edge again. The
 *   window (2 min) is exactly `maxFastTicks` × `fastMs`, so the budget runs out
 *   at the same moment the window closes.
 * - **429 backs off.** The BFF rate limiter counts per IP for all of `/api`
 *   with no per-user key, so a poll that keeps its cadence through a 429
 *   sustains the block for every user behind that IP.
 * - **It notices its own death.** Silence is the exact failure mode this
 *   feature exists to remove, so the poll may not fail silently either.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";

import {
  getAccountWatermarks,
  type AccountWatermarks,
  type ResourceWatermark,
} from "@/api/accountWatermarks";
import { ApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { useAutoRefresh } from "@/hooks/useAutoRefresh";

// ============================================================================
// Tuning
// ============================================================================

/** Relaxed cadence while nothing is moving. */
const BASELINE_MS = 15_000;

/** Cadence for the two minutes after a watermark moved: a person is doing
 *  something right now, and more writes usually follow the first one. */
const FAST_MS = 3_000;

/** How long one observed change keeps the fast tier armed. */
const FAST_WINDOW_MS = 2 * 60_000;

/** Sized to the window on purpose (40 × 3s = 2 min): the hook's fast budget
 *  and this provider's window must expire together, or one of them silently
 *  decides the cadence on its own. */
const MAX_FAST_TICKS = FAST_WINDOW_MS / FAST_MS;

/**
 * 401 = session gone, 403 = access revoked, 404 = the route is not there.
 * 404 is in the list because during a deploy the frontend can be newer than
 * the backend; without it the tab hammers a 404 every 15 seconds until reload.
 * All three are un-stopped by `resetKey` (the account id) — see `useAutoRefresh`.
 */
const STOP_STATUSES = [401, 403, 404];

const RATE_LIMITED_STATUS = 429;

/** First backoff step after a 429, doubling per further 429, capped. */
const BACKOFF_START_MS = 30_000;
const BACKOFF_MAX_MS = 5 * 60_000;

/** Consecutive failures before the poll admits it is dead. Once. */
const FAILURE_LOG_THRESHOLD = 5;

// ============================================================================
// Public types
// ============================================================================

export type WatermarkKind = "dashboards" | "data_sources";

export type WatermarkChange = {
  /** Which watermarks differ from the previously seen values, restricted to
   *  the kinds this subscriber asked for. Never empty. */
  kinds: WatermarkKind[];
  /** True when `last_mcp_write_at` also moved on this same tick. Consumers
   *  refresh on ANY change (over-refreshing is harmless by construction) but
   *  may only *tell the user* when the change came from an MCP write. */
  fromMcpWrite: boolean;
};

type ChangeHandler = (change: WatermarkChange) => void;

/** The handler lives in a ref so callers can pass it inline without
 *  resubscribing on every render — same pattern as `useAutoRefresh`. */
type HandlerRef = { current: ChangeHandler };

type Subscriber = { kinds: WatermarkKind[]; handler: HandlerRef };

type Subscribe = (kinds: WatermarkKind[], handler: HandlerRef) => () => void;

const AccountWatermarksContext = createContext<Subscribe | null>(null);

// ============================================================================
// Comparison
// ============================================================================

/**
 * Inequality, deliberately — NOT `>`. See the header: the timestamp is
 * application-written across several backend instances and can go backwards.
 * `count` is compared too because a deletion need not move `max_updated_at`.
 */
function moved(a: ResourceWatermark, b: ResourceWatermark): boolean {
  return a.count !== b.count || a.max_updated_at !== b.max_updated_at;
}

// ============================================================================
// Provider
// ============================================================================

export function AccountWatermarksProvider({
  children,
}: {
  children: ReactNode;
}): ReactElement {
  const { user, activeAccount } = useAuth();
  const accountId = activeAccount?.id;
  // Inert without an authenticated account: there is nothing to watermark, and
  // polling would only produce 401s.
  const enabled = !!user && !!accountId;

  const subscribersRef = useRef<Set<Subscriber>>(new Set());

  /** The last payload we saw. `null` until the baseline lands (rule: the first
   *  response announces nothing). */
  const previousRef = useRef<AccountWatermarks | null>(null);
  /**
   * Which account `previousRef` belongs to. After `POST /auth/switch-account`
   * the stored value is the PREVIOUS tenant's; comparing it with the new
   * account's first poll would be a guaranteed false "changed", and a
   * comparison across the tenant boundary in client state. The provider used
   * to be remounted with `key={accountId}` for this, but that key remounted
   * the whole route tree under it on every sign-in and switch (plan
   * 2026-10-28-partner-aware-invitation-flow, step 8). So the baseline is
   * scoped here instead, and the tree stays mounted.
   */
  const baselineAccountRef = useRef<string | undefined>(accountId);
  // Read by `refresh` (a timer callback with a stable identity), so the current
  // account travels through a ref, written from an effect, never during render.
  const accountIdRef = useRef(accountId);
  useEffect(() => {
    accountIdRef.current = accountId;
  }, [accountId]);

  /** 429 backoff: skip ticks until this timestamp, doubling per 429. */
  const skipUntilRef = useRef(0);
  const backoffMsRef = useRef(0);

  const consecutiveFailuresRef = useRef(0);
  const loggedDeathRef = useRef(false);

  /**
   * Timestamp until which the fast tier stays armed; `0` means baseline.
   * A *timestamp* rather than a boolean so it expires on its own and can
   * produce the false→true edge again on the next change.
   */
  const [fastUntil, setFastUntil] = useState(0);

  useEffect(() => {
    if (fastUntil === 0) return;
    // Closing the window is a timer, never a synchronous setState in the
    // effect body: `Math.max` covers the degenerate "already expired" case
    // without a cascading render.
    const timer = setTimeout(
      () => setFastUntil((current) => (current === fastUntil ? 0 : current)),
      Math.max(0, fastUntil - Date.now()),
    );
    return () => clearTimeout(timer);
  }, [fastUntil]);

  const subscribe = useCallback<Subscribe>((kinds, handler) => {
    const entry: Subscriber = { kinds, handler };
    subscribersRef.current.add(entry);
    return () => {
      subscribersRef.current.delete(entry);
    };
  }, []);

  const refresh = useCallback(async () => {
    // A different account than the baseline was taken for: forget the
    // previous tenant's values and its backoff. The first poll of the new
    // account is a fresh baseline and announces nothing.
    if (accountIdRef.current !== baselineAccountRef.current) {
      baselineAccountRef.current = accountIdRef.current;
      previousRef.current = null;
      skipUntilRef.current = 0;
      backoffMsRef.current = 0;
      consecutiveFailuresRef.current = 0;
      loggedDeathRef.current = false;
    }

    // 429 backoff. `useAutoRefresh` has no notion of this, and the cleanest
    // place is here: a skipped tick costs nothing and needs no state in the
    // hook.
    if (Date.now() < skipUntilRef.current) return;

    let next: AccountWatermarks;
    try {
      next = await getAccountWatermarks();
    } catch (err) {
      if (err instanceof ApiError && err.status === RATE_LIMITED_STATUS) {
        backoffMsRef.current =
          backoffMsRef.current === 0
            ? BACKOFF_START_MS
            : Math.min(backoffMsRef.current * 2, BACKOFF_MAX_MS);
        skipUntilRef.current = Date.now() + backoffMsRef.current;
      }
      consecutiveFailuresRef.current += 1;
      if (
        consecutiveFailuresRef.current >= FAILURE_LOG_THRESHOLD &&
        !loggedDeathRef.current
      ) {
        loggedDeathRef.current = true;
        console.error(
          `[AccountWatermarks] freshness poll failed ${consecutiveFailuresRef.current} times in a row — lists in this tab may be stale without saying so`,
          err,
        );
      }
      // Rethrow: `useAutoRefresh` owns the stop decision (STOP_STATUSES).
      throw err;
    }

    consecutiveFailuresRef.current = 0;
    loggedDeathRef.current = false;
    backoffMsRef.current = 0;
    skipUntilRef.current = 0;

    const previous = previousRef.current;
    previousRef.current = next;
    // Baseline: store it, say nothing.
    if (!previous) return;

    const kinds: WatermarkKind[] = [];
    if (moved(previous.dashboards, next.dashboards)) kinds.push("dashboards");
    if (moved(previous.data_sources, next.data_sources)) {
      kinds.push("data_sources");
    }
    if (kinds.length === 0) return;

    const fromMcpWrite = previous.last_mcp_write_at !== next.last_mcp_write_at;

    // Something is happening: (re-)open the fast window.
    setFastUntil(Date.now() + FAST_WINDOW_MS);

    // Snapshot the set: a handler may unsubscribe (or mount something that
    // subscribes) while we are iterating.
    for (const subscriber of Array.from(subscribersRef.current)) {
      const matched = kinds.filter((kind) => subscriber.kinds.includes(kind));
      if (matched.length === 0) continue;
      try {
        subscriber.handler.current({ kinds: matched, fromMcpWrite });
      } catch (err) {
        // One bad consumer must not stop the others from being told.
        console.error("[AccountWatermarks] a change handler threw", err);
      }
    }
  }, []);

  useAutoRefresh({
    enabled,
    fast: fastUntil > 0,
    refresh,
    baselineMs: BASELINE_MS,
    fastMs: FAST_MS,
    maxFastTicks: MAX_FAST_TICKS,
    stopStatuses: STOP_STATUSES,
    // Revive a poll that a 403 killed on the way out of the previous account.
    // The stored baseline is re-scoped per account inside `refresh` (see
    // `baselineAccountRef`); nothing is keyed or remounted for it.
    resetKey: accountId,
  });

  return (
    <AccountWatermarksContext.Provider value={subscribe}>
      {children}
    </AccountWatermarksContext.Provider>
  );
}

/**
 * The provider as the app mounts it. One level below `App` only because
 * `useAuth()` needs to be inside `AuthProvider`.
 *
 * Deliberately NOT keyed on the account id. That key used to reset the
 * baseline, and it remounted every route under it on each sign-in (none → id)
 * and account switch (id → id'), after `navigate()` had already changed the
 * URL: the e-mail-link callback then remounted on a URL that is no longer a
 * sign-in link and flashed "Invalid sign-in link" (plan
 * 2026-10-28-partner-aware-invitation-flow, step 8). The provider re-scopes
 * its baseline per account itself; see `AccountWatermarksContext.remount.test.tsx`.
 */
export function AccountScopedWatermarks({ children }: { children: ReactNode }): ReactElement {
  return <AccountWatermarksProvider>{children}</AccountWatermarksProvider>;
}

// ============================================================================
// Consumer hook
// ============================================================================

/**
 * Subscribe to watermark changes. `kinds` filters: the handler fires only when
 * at least one of them changed, and `change.kinds` lists exactly which.
 *
 * The handler may be an inline closure — it is held in a ref, so a new
 * identity every render does not resubscribe.
 *
 * Outside a provider this is a no-op rather than a throw: a page that renders
 * without the app shell (a component test, a standalone route) loses the
 * refresh, which is a degradation, not a corruption.
 */
export function useAccountWatermark(
  kinds: WatermarkKind[],
  handler: (change: WatermarkChange) => void,
): void {
  const subscribe = useContext(AccountWatermarksContext);

  const handlerRef = useRef(handler);
  useEffect(() => {
    handlerRef.current = handler;
  });

  // Callers pass `kinds` inline, so the array identity changes every render.
  // Key the subscription on the contents instead.
  const kindsKey = kinds.join(",");

  useEffect(() => {
    if (!subscribe) {
      // A no-op, but not a silent one in development. Silence is the exact
      // failure this feature exists to remove, and a consumer mounted outside
      // the provider fails in precisely that shape: the page simply never
      // refreshes and nobody can tell it from "nothing changed". Still not a
      // throw — a component test that renders one page without the app shell
      // should not have to build the provider.
      if (import.meta.env.DEV) {
        console.warn(
          "[AccountWatermarks] useAccountWatermark() outside AccountWatermarksProvider — this consumer will never be told about changes",
        );
      }
      return;
    }
    const list = kindsKey.split(",").filter(Boolean) as WatermarkKind[];
    if (list.length === 0) return;
    return subscribe(list, handlerRef);
  }, [subscribe, kindsKey]);
}
